// R503 (b): HOP's one sound module, WebAudio only (the repo has no audio assets). It replaces the
// Board's own ping and the kitchen TV's kitchen-tv-sound.ts: every screen that makes a sound
// imports it from here. Each player swallows its own errors: a blocked or missing audio device
// must never break a screen.

// The loudest gain any HOP sound plays at: the kitchen TV's R285 ring. `ring.loudness` 100 is
// this gain, 0 is silence.
export const MAX_GAIN = 0.3

export function createRingAudio(): AudioContext | null {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    return Ctx ? new Ctx() : null
  } catch {
    return null
  }
}

// `ring.loudness` (a whole number, 0 to 100) as a gain from 0 to MAX_GAIN.
export function loudnessGain(loudness: number): number {
  if (!Number.isFinite(loudness)) return 0
  const clamped = Math.min(100, Math.max(0, loudness))
  return (MAX_GAIN * clamped) / 100
}

// Rook #67784 (1): the Board's one-shot chime on a new order arriving, as main had it (its own
// context each time, 880 Hz sine, gain 0.05, 0.12 s; silent where the browser blocks it). It never
// repeats: the ring takes over at the pending threshold.
export function playNewOrderChime(): void {
  const ctx = createRingAudio()
  if (!ctx) return
  try {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 880
    gain.gain.value = 0.05
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.12)
    osc.onended = () => ctx.close()
  } catch {
    // audio unsupported or blocked: non-fatal, the "new" badge still shows it
  }
}

// The kitchen TV's short beep on a new task (R285).
export function playBeep(ctx: AudioContext): void {
  try {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 880
    gain.gain.value = 0.08
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.15)
  } catch {
    // quiet by design
  }
}

// The ring: three square pulses, 660 Hz / 990 Hz alternating. The kitchen TV's ready ring plays
// it at MAX_GAIN (R285); the waiting ring plays it at the owner's loudness.
export function playRing(ctx: AudioContext, gainValue: number = MAX_GAIN): void {
  try {
    const PULSE_S = 0.25
    const GAP_S = 0.1
    const freqs = [660, 990, 660]
    const t0 = ctx.currentTime
    freqs.forEach((hz, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'square'
      osc.frequency.value = hz
      gain.gain.value = gainValue
      osc.connect(gain)
      gain.connect(ctx.destination)
      const start = t0 + i * (PULSE_S + GAP_S)
      osc.start(start)
      osc.stop(start + PULSE_S)
    })
  } catch {
    // quiet by design
  }
}
